import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import { useUserStore } from '../store/useUserStore';
import { TourContext, type TourApi } from './tourContext';
import { isBlockingModalOpen, isVisibleElement, waitForElement } from './tourDom';
import {
  isTourEligible,
  pickAutoStartTour,
  resolveStepOutcome,
  resolveStepSelector,
  stepNavigation,
  tourEntryRoute,
} from './tourLogic';
import { TourOverlay } from './TourOverlay';
import type { TourDefinition } from './tourTypes';
import { tours as allTours } from './tours';
import { useOnboarding } from './useOnboardingStore';
import { useOnboardingSync } from './useOnboardingSync';
import { hasSeenTour } from './flags';

const AUTO_START_DELAY_MS = 1200;
const AUTO_START_ATTEMPTS = 8;
const REQUIRED_STEP_TIMEOUT_MS = 5000;
const OPTIONAL_STEP_TIMEOUT_MS = 2000;

interface ActiveTour {
  tour: TourDefinition;
  index: number;
  direction: 1 | -1;
  target: HTMLElement | null;
  ready: boolean;
  /** The breakpoint the current step was resolved for. */
  resolvedMobile: boolean;
  shownCount: number;
}

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isMobile = useIsMobile();
  const isAuthenticated = useAuth().isAuthenticated;
  const userType = useUserStore((s) => s.user?.userType ?? null);
  const { flags, markTourSeen } = useOnboarding();
  useOnboardingSync();

  const [active, setActive] = useState<ActiveTour | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const autoAttempted = useRef(new Set<string>());

  const audienceCtx = useMemo(
    () => ({ isAuthenticated, userType, pathname }),
    [isAuthenticated, userType, pathname],
  );
  const tours = useMemo(
    () => allTours.filter((t) => isTourEligible(t, audienceCtx)),
    [audienceCtx],
  );

  const end = useCallback(
    (current: ActiveTour, reason: 'completed' | 'skipped' | 'failed') => {
      const { tour, shownCount } = current;
      if (reason !== 'failed' || shownCount > 0) markTourSeen(tour.id, tour.version);
      setActive(null);
      const focusTarget = returnFocus.current;
      returnFocus.current = null;
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
    },
    [markTourSeen],
  );

  const begin = useCallback(
    (tour: TourDefinition) => {
      returnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const entry = tourEntryRoute(tour, router.state.location.pathname);
      if (entry) router.history.push(entry);
      setActive({
        tour,
        index: 0,
        direction: 1,
        target: null,
        ready: false,
        resolvedMobile: isMobile,
        shownCount: 0,
      });
    },
    [router, isMobile],
  );

  const needsResolve = !!active && (!active.ready || active.resolvedMobile !== isMobile);
  const activeTourId = active?.tour.id ?? null;
  const activeIndex = active?.index ?? -1;

  useEffect(() => {
    if (!needsResolve || !active) return;
    const controller = new AbortController();
    const { tour, index, direction } = active;
    const step = tour.steps[index];

    (async () => {
      const { pathname: currentPath, searchStr } = router.state.location;
      const nav = stepNavigation(step, currentPath, searchStr);
      if (nav) router.history.push(nav);

      const selector = resolveStepSelector(step, isMobile);
      const target = selector
        ? await waitForElement(selector, {
            timeout: step.optional ? OPTIONAL_STEP_TIMEOUT_MS : REQUIRED_STEP_TIMEOUT_MS,
            signal: controller.signal,
          })
        : null;
      if (controller.signal.aborted) return;

      const outcome = resolveStepOutcome(tour.steps, index, direction, !selector || !!target);
      if (outcome.kind === 'show') {
        setActive((prev) =>
          prev && prev.tour.id === tour.id && prev.index === index
            ? {
                ...prev,
                target,
                ready: true,
                resolvedMobile: isMobile,
                shownCount: prev.shownCount + 1,
              }
            : prev,
        );
      } else if (outcome.kind === 'skip') {
        setActive((prev) =>
          prev && prev.tour.id === tour.id ? { ...prev, index: outcome.next, ready: false } : prev,
        );
      } else {
        end(active, outcome.completed && active.shownCount > 0 ? 'completed' : 'failed');
      }
    })();

    return () => controller.abort();
    // `active` is read for its identity fields only; re-running on every change
    // (e.g. the target being set) would restart the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsResolve, activeTourId, activeIndex, isMobile, router, end]);

  // Re-find the target if a re-render replaces or hides it mid-step.
  const shownTarget = active?.ready ? active.target : null;
  useEffect(() => {
    if (!shownTarget) return;
    const interval = window.setInterval(() => {
      if (!shownTarget.isConnected || !isVisibleElement(shownTarget)) {
        setActive((prev) =>
          prev && prev.target === shownTarget ? { ...prev, ready: false } : prev,
        );
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, [shownTarget]);

  const isActive = !!active;
  useEffect(() => {
    if (isActive || !isAuthenticated) return;
    let attempts = 0;
    let interval: number | undefined;
    const tryStart = () => {
      attempts += 1;
      const tour = pickAutoStartTour(
        allTours.filter((t) => !autoAttempted.current.has(t.id)),
        { ...audienceCtx, flags, modalOpen: isBlockingModalOpen(), tourActive: false },
      );
      if (tour) {
        autoAttempted.current.add(tour.id);
        window.clearInterval(interval);
        begin(tour);
      } else if (attempts >= AUTO_START_ATTEMPTS) {
        window.clearInterval(interval);
      }
    };
    const timer = window.setTimeout(() => {
      tryStart();
      interval = window.setInterval(tryStart, AUTO_START_DELAY_MS);
    }, AUTO_START_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
    };
  }, [isActive, isAuthenticated, audienceCtx, flags, begin]);

  const start = useCallback(
    (id: string) => {
      const tour = allTours.find((t) => t.id === id);
      if (tour && tour.steps.length > 0) begin(tour);
    },
    [begin],
  );

  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const autoStart = useCallback(
    (id: string) => {
      const tour = allTours.find((t) => t.id === id);
      if (!tour || !isAuthenticated || autoAttempted.current.has(id)) return;
      if (hasSeenTour(flags, tour.id, tour.version) || !isTourEligible(tour, audienceCtx)) return;
      window.setTimeout(() => {
        if (activeRef.current || autoAttempted.current.has(id) || isBlockingModalOpen()) return;
        autoAttempted.current.add(id);
        begin(tour);
      }, AUTO_START_DELAY_MS);
    },
    [isAuthenticated, flags, audienceCtx, begin],
  );

  const stop = useCallback(() => {
    if (active) end(active, 'skipped');
  }, [active, end]);

  const next = useCallback(() => {
    if (!active) return;
    if (active.index + 1 >= active.tour.steps.length) {
      end(active, 'completed');
      return;
    }
    setActive({ ...active, index: active.index + 1, direction: 1, ready: false });
  }, [active, end]);

  const back = useCallback(() => {
    if (!active || active.index === 0) return;
    setActive({ ...active, index: active.index - 1, direction: -1, ready: false });
  }, [active]);

  const api = useMemo<TourApi>(
    () => ({ start, autoStart, stop, isActive, activeTourId, tours }),
    [start, autoStart, stop, isActive, activeTourId, tours],
  );

  return (
    <TourContext.Provider value={api}>
      {children}
      {active && (
        <TourOverlay
          step={active.tour.steps[active.index]}
          index={active.index}
          total={active.tour.steps.length}
          target={active.target}
          ready={active.ready}
          isMobile={isMobile}
          onNext={next}
          onBack={back}
          onSkip={stop}
        />
      )}
    </TourContext.Provider>
  );
};
